/**
 * SSE (Server-Sent Events) 工具类
 * 用于处理流式 API 调用
 */

import { getAcsToken } from "@utils/initParis";

export interface SSEOptions {
    onMessage: (data: any) => void;
    onError?: (error: Error) => void;
    onDone?: () => void;
    headers?: Record<string, string>;
}

/**
 * 使用 fetch 实现的 SSE 客户端
 * 相比 EventSource 更灵活，支持 POST 请求和自定义 headers
 */
export class SSEClient {
    private controller: AbortController | null = null;
    private reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
    private isManuallyClosed = false; // 标记是否为用户主动关闭

    /**
     * 发起 SSE 请求
     * @param url 请求 URL
     * @param options 配置项
     * @param method 请求方法，默认 GET
     * @param body 请求体（POST 时使用）
     */
    async connect(url: string, options: SSEOptions, method: "GET" | "POST" = "GET", body?: any): Promise<void> {
        this.controller = new AbortController();
        const token = await getAcsToken();
        try {
            const response = await fetch(url, {
                method,
                headers: {
                    "Content-Type": "application/json",
                    Accept: "text/event-stream",
                    'Acs-Token': token as string,
                    ...options.headers,
                },
                body: body ? JSON.stringify(body) : undefined,
                signal: this.controller.signal,
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            if (!response.body) {
                throw new Error("Response body is null");
            }

            this.reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            let currentEvent = ""; // 记录当前事件类型

            while (true) {
                const { done, value } = await this.reader.read();

                if (done) {
                    // 如果是用户主动关闭，调用 onDone 而不是 onError
                    if (this.isManuallyClosed) {
                        console.log("SSE 连接已被用户主动关闭");
                        options.onDone?.();
                    } else {
                        // Stream 关闭，但没有收到 event: done，视为异常中断
                        options.onError?.(new Error("SSE connection closed unexpectedly"));
                    }
                    break;
                }

                // 解码数据并追加到缓冲区
                buffer += decoder.decode(value, { stream: true });

                // 处理缓冲区中的完整消息
                const lines = buffer.split("\n");
                buffer = lines.pop() || ""; // 保留不完整的行

                for (const line of lines) {
                    // 处理 event: 字段
                    if (line.startsWith("event: ")) {
                        currentEvent = line.slice(7).trim();

                        // 如果收到 event: end
                        if (currentEvent === "end") {
                            this.close(false); // 正常结束，非手动关闭
                            options.onDone?.();
                            return;
                        }
                    }
                    // 处理 data: 字段
                    else if (line.startsWith("data: ")) {
                        try {
                            const jsonStr = line.slice(6).trim();
                            if (jsonStr) {
                                const data = JSON.parse(jsonStr);
                                options.onMessage(data);
                            }
                        } catch (error) {
                            console.error("解析 SSE 数据失败:", error, line);
                        }
                        // 处理完 data 后重置 event
                        currentEvent = "";
                    }
                }
            }
        } catch (error: any) {
            if (error.name === "AbortError") {
                console.log("SSE 连接已中止");
            } else {
                console.error("SSE 连接错误:", error);
                options.onError?.(error);
            }
        }
    }

    /**
     * 关闭 SSE 连接
     * @param manual 是否为用户主动关闭，默认 true
     */
    close(manual = true): void {
        if (manual) {
            this.isManuallyClosed = true;
        }
        if (this.reader) {
            this.reader.cancel();
            this.reader = null;
        }
        if (this.controller) {
            this.controller.abort();
            this.controller = null;
        }
    }
}

/**
 * 便捷函数：创建 SSE 连接
 */
export function createSSEConnection(
    url: string,
    options: SSEOptions,
    method: "GET" | "POST" = "GET",
    body?: any
): SSEClient {
    const client = new SSEClient();
    client.connect(url, options, method, body);
    return client;
}
